/** The supplemental witness contains private keys. Only a bundled local worker
 * may receive it; no fee endpoint accepts this witness. */
function createPrivateFeeProver(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
  const worker = new Worker(
    new URL("/provers/private-fees-v2/worker.js", window.location.origin),
    { type: "module" },
  );
  let readyResolve!: () => void, readyReject!: (error: Error) => void;
  const ready = new Promise<void>((resolve, reject) => {
    readyResolve = resolve;
    readyReject = reject;
  });
  void ready.catch(() => {});
  let resolveProof: ((proof: `0x${string}`) => void) | undefined;
  let rejectProof: ((error: Error) => void) | undefined;
  let started = false;
  let finished = false;
  const finish = (error?: Error, proof?: `0x${string}`) => {
    if (finished) return;
    finished = true;
    worker.terminate();
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
    if (error) {
      readyReject(error);
      rejectProof?.(error);
    } else resolveProof?.(proof!);
  };
  const abort = () => finish(new DOMException("Cancelled", "AbortError"));
  const startTimeout = () =>
    setTimeout(
      () =>
        finish(
          new Error("Preparing the transaction timed out. Please try again."),
        ),
      180000,
    );
  let timeout = startTimeout();
  signal?.addEventListener("abort", abort, { once: true });
  worker.onerror = () =>
    finish(new Error("Unable to prepare the transaction on this device."));
  worker.onmessage = ({ data }) => {
    if (finished) return;
    if (data?.ready === true) {
      readyResolve();
      return;
    }
    if (data?.error)
      return finish(
        new Error("Unable to prepare the transaction on this device."),
      );
    if (
      typeof data?.proof !== "string" ||
      !/^0x[0-9a-f]{512}$/.test(data.proof)
    )
      return finish(new Error("Transaction preparation failed."));
    finish(undefined, data.proof);
  };
  // No private material is sent while public key loading overlaps the main proof.
  try {
    worker.postMessage({ type: "prepare" });
  } catch {
    finish(new Error("Transaction preparation failed."));
  }
  return {
    active: () => !finished,
    // A standby worker contains public parameters only. Give an acquired job
    // its own cancellation scope and full proving deadline.
    adopt(nextSignal?: AbortSignal) {
      // Startup can fail synchronously while posting the public prepare message.
      // Never attach resources again after finish() has cleaned up the session.
      if (finished) throw new Error("Transaction preparation failed.");
      signal?.removeEventListener("abort", abort);
      signal = nextSignal;
      if (signal?.aborted) {
        abort();
        throw new DOMException("Cancelled", "AbortError");
      }
      signal?.addEventListener("abort", abort, { once: true });
      clearTimeout(timeout);
      timeout = startTimeout();
    },
    dispose: () => finish(new Error("Transaction preparation was cancelled.")),
    async prove(witness: unknown): Promise<`0x${string}`> {
      if (started || finished)
        throw new Error("Transaction preparation was cancelled.");
      started = true;
      await ready;
      if (finished) throw new Error("Transaction preparation was cancelled.");
      return new Promise((resolve, reject) => {
        resolveProof = resolve;
        rejectProof = reject;
        try {
          worker.postMessage({ type: "prove", witness });
        } catch {
          finish(new Error("Transaction preparation failed."));
        }
      });
    },
  };
}

let standby: ReturnType<typeof createPrivateFeeProver> | undefined;
let standbyTimeout: ReturnType<typeof setTimeout> | undefined;

/** Warm only public assets while the user fills in Send. Never retain a worker
 * after it has received a witness. Closing the form releases unused warmup. */
export function warmPrivateFeeProver(): () => void {
  if (standby?.active()) return () => {};
  let candidate: ReturnType<typeof createPrivateFeeProver>;
  try {
    candidate = createPrivateFeeProver();
  } catch {
    return () => {};
  }
  standby = candidate;
  clearTimeout(standbyTimeout);
  const release = () => {
    if (standby !== candidate) return;
    standby = undefined;
    clearTimeout(standbyTimeout);
    candidate.dispose();
  };
  standbyTimeout = setTimeout(release, 120000);
  return release;
}

export function preparePrivateFeeProver(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
  const candidate = standby;
  standby = undefined;
  clearTimeout(standbyTimeout);
  const session = candidate?.active() ? candidate : createPrivateFeeProver();
  session.adopt(signal);
  return { dispose: session.dispose, prove: session.prove };
}
